'use client';
import React, { useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Upload, FileText, CheckCircle2, XCircle,
  AlertTriangle, ArrowRight, RefreshCw, Info,
  Calendar, Building2, CreditCard, Trash2
} from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { CreditMap } from '@/lib/types';
import { mockCreditMapAnterior, mockCreditMapAtual } from '@/lib/mockData';
import { parseBancodePortugalPDF } from '@/lib/pdfParser';
import { buildComparisonReport } from '@/lib/reportBuilder';
import StatusBadge from '@/components/ui/StatusBadge';
import { formatEuro, formatDate } from '@/lib/formatters';

type FileSlot = 'anterior' | 'atual';

interface ParsedFileState {
  file: File | null;
  map: CreditMap | null;
  status: 'idle' | 'parsing' | 'success' | 'error';
  errorMsg: string | null;
}

const initialState: ParsedFileState = {
  file: null,
  map: null,
  status: 'idle',
  errorMsg: null,
};

export default function DataImportContent() {
  const router = useRouter();
  const { setMapAnterior, setMapAtual, setReport, loadMockData } = useAppStore();

  const [anteriorState, setAnteriorState] = useState<ParsedFileState>(initialState);
  const [atualState, setAtualState] = useState<ParsedFileState>(initialState);
  const [dragOver, setDragOver] = useState<FileSlot | null>(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [expandedInstitution, setExpandedInstitution] = useState<string | null>(null);

  const anteriorInputRef = useRef<HTMLInputElement>(null);
  const atualInputRef = useRef<HTMLInputElement>(null);

  const parsePDF = useCallback(async (file: File, slot: FileSlot) => {
    const setter = slot === 'anterior' ? setAnteriorState : setAtualState;
    setter((prev) => ({ ...prev, file, status: 'parsing', errorMsg: null }));

    try {
      const map = await parseBancodePortugalPDF(file);
      setter({ file, map, status: 'success', errorMsg: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao processar o ficheiro PDF.';
      setter({ file, map: null, status: 'error', errorMsg: msg });
    }
  }, []);

  const handleFileSelect = useCallback((file: File, slot: FileSlot) => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext !== '.pdf') {
      const setter = slot === 'anterior' ? setAnteriorState : setAtualState;
      setter({ file, map: null, status: 'error', errorMsg: 'Formato inválido. Apenas ficheiros PDF são aceites.' });
      return;
    }
    parsePDF(file, slot);
  }, [parsePDF]);

  const handleDrop = useCallback((e: React.DragEvent, slot: FileSlot) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file, slot);
  }, [handleFileSelect]);

  const handleClear = (slot: FileSlot) => {
    if (slot === 'anterior') setAnteriorState(initialState);
    else setAtualState(initialState);
  };

  const handleLoadDemo = () => {
    setLoadingDemo(true);
    setTimeout(() => {
      loadMockData();
      setAnteriorState({ file: null, map: mockCreditMapAnterior, status: 'success', errorMsg: null });
      setAtualState({ file: null, map: mockCreditMapAtual, status: 'success', errorMsg: null });
      setLoadingDemo(false);
    }, 900);
  };

  const handleProceed = () => {
    if (!anteriorState.map || !atualState.map) return;
    setNavigating(true);
    setMapAnterior(anteriorState.map);
    setMapAtual(atualState.map);
    const report = buildComparisonReport(anteriorState.map, atualState.map);
    setReport(report);
    setTimeout(() => {
      router.push('/comparison-report');
    }, 400);
  };

  const bothLoaded = anteriorState.status === 'success' && atualState.status === 'success';

  return (
    <div className="fade-in">
      {/* Page header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/ZEZERECONTA_04-1784205012431.png"
              alt="Zezereconta"
              className="h-16 object-contain"
            />
            <h1 className="text-2xl font-semibold text-foreground">Importar Mapas de Crédito</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Carregue os mapas PDF de dois meses consecutivos do Banco de Portugal para gerar o relatório comparativo.
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-info-subtle border border-blue-200 rounded-lg px-4 py-3 mb-8">
        <Info size={16} className="text-[color:var(--info)] mt-0.5 shrink-0" />
        <p className="text-sm text-[color:var(--info)]">
          Carregue os mapas em formato <strong>PDF</strong> exportados do Banco de Portugal.
          O mês e ano são detectados automaticamente a partir do canto superior direito de cada mapa.
          As colunas esperadas são: <strong>Produto Financeiro</strong>, <strong>Início</strong>, <strong>Fim</strong> e <strong>Total em Dívida</strong>.
        </p>
      </div>

      {/* Upload zones grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <UploadZone
          slot="anterior"
          label="Mês 1"
          description="Mapa PDF do mês base para comparação"
          state={anteriorState}
          isDragOver={dragOver === 'anterior'}
          inputRef={anteriorInputRef}
          onDrop={(e) => handleDrop(e, 'anterior')}
          onDragOver={(e) => { e.preventDefault(); setDragOver('anterior'); }}
          onDragLeave={() => setDragOver(null)}
          onFileSelect={(f) => handleFileSelect(f, 'anterior')}
          onClear={() => handleClear('anterior')}
        />
        <UploadZone
          slot="atual"
          label="Mês 2"
          description="Mapa PDF do mês mais recente"
          state={atualState}
          isDragOver={dragOver === 'atual'}
          inputRef={atualInputRef}
          onDrop={(e) => handleDrop(e, 'atual')}
          onDragOver={(e) => { e.preventDefault(); setDragOver('atual'); }}
          onDragLeave={() => setDragOver(null)}
          onFileSelect={(f) => handleFileSelect(f, 'atual')}
          onClear={() => handleClear('atual')}
        />
      </div>

      {/* Validation summary */}
      {(anteriorState.status !== 'idle' || atualState.status !== 'idle') && (
        <div className="bg-card border border-border rounded-lg p-5 mb-8">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-primary" />
            Resumo de Validação
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ValidationItem
              label="Mês 1"
              value={anteriorState.map?.mesLabel ?? '—'}
              status={anteriorState.status}
            />
            <ValidationItem
              label="Mês 2"
              value={atualState.map?.mesLabel ?? '—'}
              status={atualState.status}
            />
            <ValidationItem
              label="Instituições (anterior)"
              value={anteriorState.map ? `${anteriorState.map.institutions.length}` : '—'}
              status={anteriorState.status}
            />
            <ValidationItem
              label="Instituições (atual)"
              value={atualState.map ? `${atualState.map.institutions.length}` : '—'}
              status={atualState.status}
            />
          </div>
        </div>
      )}

      {/* Preview tables */}
      {bothLoaded && anteriorState.map && atualState.map && (
        <div className="space-y-6 mb-8">
          <PreviewTable
            map={anteriorState.map}
            label="Mês 1"
            expandedInstitution={expandedInstitution}
            onToggleInstitution={(id) => setExpandedInstitution(expandedInstitution === id ? null : id)}
          />
          <PreviewTable
            map={atualState.map}
            label="Mês 2"
            expandedInstitution={expandedInstitution}
            onToggleInstitution={(id) => setExpandedInstitution(expandedInstitution === id ? null : id)}
          />
        </div>
      )}

      {/* Proceed button */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground">
          {bothLoaded
            ? 'Ambos os mapas foram carregados e validados com sucesso.'
            : 'Carregue ambos os mapas PDF para continuar.'}
        </p>
        <button
          onClick={handleProceed}
          disabled={!bothLoaded || navigating}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg
            hover:bg-primary/90 active:scale-95 transition-all duration-150
            disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100"
        >
          {navigating ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <>
              Gerar Relatório Comparativo
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface UploadZoneProps {
  slot: FileSlot;
  label: string;
  description: string;
  state: ParsedFileState;
  isDragOver: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onFileSelect: (f: File) => void;
  onClear: () => void;
}

function UploadZone({
  label, description, state, isDragOver,
  inputRef, onDrop, onDragOver, onDragLeave, onFileSelect, onClear
}: UploadZoneProps) {
  const isParsing = state.status === 'parsing';
  const isSuccess = state.status === 'success';
  const isError = state.status === 'error';

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-[12px] text-muted-foreground">{description}</p>
        </div>
        {isSuccess && <StatusBadge status="valido" />}
        {isError && <StatusBadge status="erro" />}
        {isParsing && <StatusBadge status="pendente" label="A processar..." />}
      </div>

      {/* Drop area */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`
          relative p-8 flex flex-col items-center justify-center
          border-2 border-dashed rounded-b-lg mx-4 my-4
          transition-all duration-200 cursor-pointer
          ${isDragOver ? 'drop-zone-active' : 'border-border hover:border-primary/40 hover:bg-muted/20'}
          ${isSuccess ? 'border-green-300 bg-positive-subtle hover:border-primary/60' : ''}
          ${isError ? 'border-red-300 bg-negative-subtle' : ''}
        `}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFileSelect(f);
          }}
        />

        {isParsing && (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <p className="text-sm text-muted-foreground">A processar PDF...</p>
            <p className="text-xs text-muted-foreground">A extrair dados do mapa...</p>
          </div>
        )}

        {!isParsing && !isSuccess && !isError && (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
              <Upload size={22} className="text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Arraste o ficheiro PDF aqui</p>
              <p className="text-xs text-muted-foreground mt-0.5">ou clique para seleccionar</p>
            </div>
            <p className="text-[11px] text-muted-foreground bg-muted px-3 py-1 rounded-full">
              Apenas ficheiros .pdf
            </p>
          </div>
        )}

        {isSuccess && state.map && (
          <div className="flex flex-col items-center gap-2 text-center w-full">
            <CheckCircle2 size={28} className="text-positive" />
            <p className="text-sm font-semibold text-foreground">
              {state.file?.name ?? state.map.fileName}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {state.map.mesLabel}
              </span>
              <span className="flex items-center gap-1">
                <Building2 size={12} />
                {state.map.institutions.length} instituições
              </span>
              <span className="flex items-center gap-1">
                <CreditCard size={12} />
                {state.map.institutions.reduce((s, i) => s + i.loans.length, 0)} empréstimos
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground tabular-nums mt-1">
              Total: {formatEuro(state.map.totalGeral)}
            </p>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-2 text-center">
            <XCircle size={28} className="text-negative" />
            <p className="text-sm font-medium text-negative">{state.errorMsg}</p>
            <button
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              className="text-xs text-primary underline mt-1"
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>

      {/* Clear button */}
      {(isSuccess || isError) && (
        <div className="px-4 pb-3 flex justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-negative transition-colors duration-150"
          >
            <Trash2 size={12} />
            Remover ficheiro
          </button>
        </div>
      )}
    </div>
  );
}

function ValidationItem({ label, value, status }: { label: string; value: string; status: string }) {
  const icon = status === 'success'
    ? <CheckCircle2 size={14} className="text-positive" />
    : status === 'error'
    ? <XCircle size={14} className="text-negative" />
    : status === 'parsing'
    ? <RefreshCw size={14} className="text-primary animate-spin" />
    : <AlertTriangle size={14} className="text-muted-foreground" />;

  return (
    <div className="bg-muted/40 rounded-lg px-3 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-sm font-semibold text-foreground tabular-nums">{value}</p>
    </div>
  );
}

interface PreviewTableProps {
  map: CreditMap;
  label: string;
  expandedInstitution: string | null;
  onToggleInstitution: (id: string) => void;
}

function PreviewTable({ map, label, expandedInstitution, onToggleInstitution }: PreviewTableProps) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">
            Pré-visualização — {label}: {map.mesLabel}
          </h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>{map.institutions.length} instituições</span>
          <span>·</span>
          <span>{map.institutions.reduce((s, i) => s + i.loans.length, 0)} empréstimos</span>
          <span>·</span>
          <span className="font-semibold text-foreground tabular-nums">{formatEuro(map.totalGeral)}</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 border-b border-border">
              <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide w-8"></th>
              <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Instituição</th>
              <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Nº Empréstimos</th>
              <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Total em Dívida</th>
              <th className="text-center px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Estado</th>
            </tr>
          </thead>
          <tbody>
            {map.institutions.map((inst) => (
              <React.Fragment key={`preview-${map.id}-${inst.id}`}>
                <tr
                  className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors duration-100"
                  onClick={() => onToggleInstitution(inst.id)}
                >
                  <td className="px-4 py-3 text-muted-foreground">
                    <span className={`inline-block transition-transform duration-200 ${expandedInstitution === inst.id ? 'rotate-90' : ''}`}>
                      ›
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">{inst.nome}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{inst.loans.length}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-foreground">{formatEuro(inst.totalEmDivida)}</td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status="valido" />
                  </td>
                </tr>
                {expandedInstitution === inst.id && (
                  <tr key={`expanded-${map.id}-${inst.id}`}>
                    <td colSpan={5} className="px-0 py-0 bg-muted/20">
                      <div className="px-12 py-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr>
                              <th className="text-left pb-2 font-medium text-muted-foreground">Produto Financeiro</th>
                              <th className="text-left pb-2 font-medium text-muted-foreground">Início</th>
                              <th className="text-left pb-2 font-medium text-muted-foreground">Fim</th>
                              <th className="text-right pb-2 font-medium text-muted-foreground">Total em Dívida</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inst.loans.map((loan) => (
                              <tr key={`loan-preview-${map.id}-${loan.id}`} className="border-t border-border/50">
                                <td className="py-1.5 pr-4 text-foreground">{loan.produtoFinanceiro}</td>
                                <td className="py-1.5 pr-4 text-muted-foreground tabular-nums">{formatDate(loan.inicio)}</td>
                                <td className="py-1.5 pr-4 text-muted-foreground tabular-nums">{formatDate(loan.fim)}</td>
                                <td className="py-1.5 text-right tabular-nums font-medium text-foreground">{formatEuro(loan.totalEmDivida)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted/40 border-t border-border">
              <td className="px-4 py-3" />
              <td className="px-4 py-3 text-sm font-semibold text-foreground">Total Geral</td>
              <td className="px-4 py-3 text-right text-sm tabular-nums text-muted-foreground">
                {map.institutions.reduce((s, i) => s + i.loans.length, 0)}
              </td>
              <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-foreground">
                {formatEuro(map.totalGeral)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}